// @givmo/design-system — a tab strip's sideways lane (CBG-DS2).
//
// A tab strip never wraps a label and never widens its page: where it cannot fit, it scrolls sideways
// inside its own lane. `Tabs.List` is one. A hand-rolled strip that mirrors the tabs (the Board
// meeting page's link-tabs) wears the same classes and keeps its current item in view the same way,
// so the two cannot drift.

/**
 * The lane. `min-w-0` lets it be narrower than its one-line items, so a strip that cannot fit
 * scrolls here instead of widening the page. The panning stays in the lane (`overscroll-x-contain`:
 * a trackpad swipe past its end does not go back a page), and it shows no scrollbar: an item cut at
 * the lane's edge says there is more. Where every item fits, nothing scrolls. Its items must neither
 * shrink nor wrap (`shrink-0 whitespace-nowrap`), and a focus indicator on them must be drawn inside
 * (`INSET_FOCUS_RING`): the lane clips anything outside it.
 */
export const TAB_LANE =
  'min-w-0 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * The lane `scrollLeft` that brings `item` fully into view with the least movement, or `null` when
 * it already is. An item wider than the lane aligns its start. Measured from the boxes, so `item`
 * may sit at any depth inside the lane.
 */
export function laneScrollToReveal(item: Element, lane: HTMLElement): number | null {
  const laneBox = lane.getBoundingClientRect();
  const box = item.getBoundingClientRect();
  const start = box.left - laneBox.left - lane.clientLeft + lane.scrollLeft;
  const end = start + box.width;
  const visibleEnd = lane.scrollLeft + lane.clientWidth;
  if (start >= lane.scrollLeft && end <= visibleEnd) return null;
  if (start < lane.scrollLeft || box.width >= lane.clientWidth) return start;
  return end - lane.clientWidth;
}

/**
 * Keep `item` whole inside the lane. A deep-linked or preselected later item otherwise renders past
 * a phone's edge, and an item clicked at the edge stays cut. Scrolls ONLY the lane, never the page
 * (as `scrollIntoView` would).
 */
export function revealInLane(lane: HTMLElement, item: Element): void {
  const next = laneScrollToReveal(item, lane);
  if (next !== null) lane.scrollLeft = next;
}
